import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import {
    Building2,
    TrendingUp,
    CheckCircle,
    XCircle,
    Clock,
    ArrowUpRight,
} from 'lucide-react'
import Paper from '@/components/Paper'
import { DashboardOverview } from '@/modules/Service/services/sla'
import { TicketSLASummary } from '@/modules/Service/types/sla'
import StatCard from '../components/stat-card'
import { formatPercent, meetsThreshold } from '../../Shared/utils/format-value'
import { paths } from '@/utils/path'

interface KpiGridProps {
    overview: DashboardOverview
    compliancePercentRaw: number
    ticketSummary: TicketSLASummary | undefined
}

/** Full-width approaching banner shown only on <md */
const ApproachingBanner = ({ count }: { count: number }) => {
    const { t } = useTranslation('services')
    return (
        <div className="md:hidden sticky top-0 z-10 w-full">
            <Paper className="border border-gray-100">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-50 text-orange-600 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs text-muted-foreground truncate">
                            {t(
                                'sla.dashboard.approaching.label',
                                'Sắp đến hạn'
                            )}
                        </p>
                        <p className="text-xl font-semibold text-foreground">
                            {count}
                        </p>
                    </div>
                    <Link to={paths.serviceSLADashboard}>
                        <ArrowUpRight className="w-4 h-4 text-muted-foreground" />
                    </Link>
                </div>
            </Paper>
        </div>
    )
}

const KpiGrid = ({
    overview,
    compliancePercentRaw,
    ticketSummary,
}: KpiGridProps) => {
    const { t } = useTranslation('services')
    const showApproaching = ticketSummary && ticketSummary.approaching_count > 0

    return (
        <>
            {/* Mobile-only sticky banner */}
            {showApproaching && (
                <ApproachingBanner count={ticketSummary.approaching_count} />
            )}

            {/* KPI grid: 2 cols mobile, 4 cols md, 5 cols lg (when approaching present) */}
            <div
                className={`grid grid-cols-2 gap-2 ${
                    showApproaching
                        ? 'md:grid-cols-3 lg:grid-cols-5'
                        : 'md:grid-cols-4'
                }`}
            >
                <StatCard
                    title="Dịch vụ có SLA"
                    value={overview.total_services_with_sla}
                    icon={Building2}
                    color="info"
                />
                <StatCard
                    title="Uptime trung bình"
                    value={formatPercent(overview.avg_uptime_percent, 'ui', 2)}
                    icon={TrendingUp}
                    color={
                        meetsThreshold(overview.avg_uptime_percent, 99.5)
                            ? 'success'
                            : 'warning'
                    }
                    tooltip="Tỉ lệ thời gian dịch vụ hoạt động trong kỳ. Đã trừ thời gian bảo trì có lịch."
                />
                <StatCard
                    title={t(
                        'sla.dashboard.kpi.services_meeting',
                        'Số dịch vụ đạt mức 95%'
                    )}
                    value={overview.services_meeting_sla}
                    subValue={`${formatPercent(compliancePercentRaw, 'ui', 2)} tuân thủ`}
                    icon={CheckCircle}
                    color="success"
                    tooltip="Số dịch vụ đạt SLA chia cho tổng số dịch vụ có cấu hình SLA, theo kỳ đang chọn."
                />
                <StatCard
                    title={t(
                        'sla.dashboard.kpi.services_breaching',
                        'Số dịch vụ chưa đạt 95%'
                    )}
                    value={overview.services_breaching_sla}
                    subValue={`/${overview.total_services_with_sla} dịch vụ`}
                    icon={XCircle}
                    color={
                        overview.services_breaching_sla > 0
                            ? 'danger'
                            : 'success'
                    }
                />

                {/* 5th card: approaching — desktop only, conditional */}
                {showApproaching && (
                    <div className="hidden md:block">
                        <StatCard
                            title={t(
                                'sla.dashboard.approaching.label',
                                'Sắp đến hạn'
                            )}
                            value={ticketSummary.approaching_count}
                            subValue={t(
                                'sla.dashboard.approaching.subhint',
                                'Bạn còn ít thời gian xử lý các ticket sau'
                            )}
                            icon={Clock}
                            color="approaching"
                            tooltip="Ticket đã dùng 80–100% thời gian SLA"
                        />
                    </div>
                )}
            </div>
        </>
    )
}

// Threshold exported for tests
export { meetsThreshold }
export default KpiGrid
